import { DOCUMENT } from '@angular/common';
import { inject, Injectable, NgZone, Renderer2, RendererFactory2 } from '@angular/core';
import {
  NzMessageContentType,
  NzMessageDataOptions,
  NzMessageRef,
  NzMessageService,
  NzMessageType,
} from 'ng-zorro-antd/message';

@Injectable()
export class CloseableMessageService extends NzMessageService {
  private readonly document = inject(DOCUMENT);
  private readonly ngZone = inject(NgZone);
  private readonly renderer: Renderer2 = inject(RendererFactory2).createRenderer(null, null);

  override success(content: NzMessageContentType, options?: NzMessageDataOptions): NzMessageRef {
    return this.withCloseButton(super.success(content, options));
  }

  override error(content: NzMessageContentType, options?: NzMessageDataOptions): NzMessageRef {
    return this.withCloseButton(super.error(content, options));
  }

  override info(content: NzMessageContentType, options?: NzMessageDataOptions): NzMessageRef {
    return this.withCloseButton(super.info(content, options));
  }

  override warning(content: NzMessageContentType, options?: NzMessageDataOptions): NzMessageRef {
    return this.withCloseButton(super.warning(content, options));
  }

  override loading(content: NzMessageContentType, options?: NzMessageDataOptions): NzMessageRef {
    return this.withCloseButton(super.loading(content, options));
  }

  override create(type: NzMessageType | string, content: NzMessageContentType, options?: NzMessageDataOptions): NzMessageRef {
    return this.withCloseButton(super.create(type, content, options));
  }

  private withCloseButton(ref: NzMessageRef): NzMessageRef {
    this.ngZone.runOutsideAngular(() => {
      requestAnimationFrame(() => this.decorateRenderedMessages());
    });

    return ref;
  }

  private decorateRenderedMessages(): void {
    const notices = Array.from(this.document.querySelectorAll<HTMLElement>('.ant-message-notice'));

    if (!notices.length || !this.container?.instances?.length) {
      return;
    }

    const instances = this.container.instances;

    notices.forEach((notice, index) => {
      if (notice.dataset['electaCloseable'] === 'true') {
        return;
      }

      const instance = instances[index];
      const messageId = instance?.messageId;
      const content = notice.querySelector<HTMLElement>('.ant-message-notice-content');

      if (!messageId || !content) {
        return;
      }

      this.renderer.setAttribute(notice, 'data-electa-closeable', 'true');
      this.renderer.setAttribute(notice, 'data-electa-message-id', messageId);

      const button = this.renderer.createElement('button') as HTMLButtonElement;
      this.renderer.addClass(button, 'ant-message-close-x');
      this.renderer.setAttribute(button, 'type', 'button');
      this.renderer.setAttribute(button, 'aria-label', 'Close notification');
      this.renderer.setAttribute(button, 'title', 'Close');
      this.renderer.setProperty(button, 'textContent', '×');
      this.renderer.listen(button, 'click', (event: MouseEvent) => {
        event.stopPropagation();
        this.ngZone.run(() => this.remove(messageId));
      });

      this.renderer.appendChild(content, button);
    });
  }
}
